// pages/strategic-playbook/tools/barbell-allocator.js
// ════════════════════════════════════════════════════
// BARBELL ALLOCATOR (Taleb 80/20)
// ════════════════════════════════════════════════════
// Antifragile capital allocation: extreme-safe + extreme-
// risk, avoid the middle. The middle gets crushed by tails.
//
// Connects to: Taleb (antifragile)
// ════════════════════════════════════════════════════

import { useState, useMemo } from 'react';
import Head from 'next/head';
import Link from 'next/link';

const RISK_PROFILES = [
  { id: 'conservative', label: 'Conservative', safePct: 90, hint: 'Late-career or capital-preservation focus' },
  { id: 'balanced-safe', label: 'Balanced (safe-leaning)', safePct: 80, hint: "Taleb's classic recommendation" },
  { id: 'balanced', label: 'Balanced', safePct: 70, hint: 'Mid-career, moderate risk' },
  { id: 'aggressive', label: 'Aggressive', safePct: 60, hint: 'Long horizon, high risk-tolerance' },
  { id: 'pure-risk', label: 'Pure asymmetric', safePct: 50, hint: 'Founder / early-career, high upside chase' },
];

export default function BarbellAllocator() {
  const [totalCapital, setTotalCapital] = useState(500000);
  const [profile, setProfile] = useState('balanced-safe');
  const [horizon, setHorizon] = useState(20);
  const [tailRiskBudget, setTailRiskBudget] = useState(2);

  const result = useMemo(() => {
    return allocate({ totalCapital, profile, horizon, tailRiskBudget: tailRiskBudget / 100 });
  }, [totalCapital, profile, horizon, tailRiskBudget]);

  return (
    <div className="sp-root">
      <Head>
        <title>Barbell Allocator · Strategic Intelligence</title>
      </Head>

      <div className="sp-page">
        <ToolTopbar />

        <div style={{ marginBottom: 32 }}>
          <Link href="/strategic-playbook/tools" className="sp-reader-back" style={{ marginBottom: 16 }}>
            ← Tools
          </Link>
          <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.20em', color: 'var(--sp-aged-brass)', marginBottom: 14 }}>
            Decision Sizing · Calculator
          </div>
          <h1 style={{ fontFamily: 'var(--sp-font-serif)', fontWeight: 500, fontSize: 32, lineHeight: 1.2, color: 'var(--sp-warm-cream)', margin: '0 0 14px 0', letterSpacing: '-0.01em' }}>
            Barbell Allocator
          </h1>
          <p style={{ fontSize: 14, lineHeight: 1.65, color: 'rgba(232, 221, 196, 0.78)', maxWidth: 700, margin: 0 }}>
            Taleb-style allocation: extreme-safe + extreme-risk, avoid the middle. <strong>The
            middle is fragile</strong> — it has neither the safety of cash nor the upside of
            asymmetric bets. Combined position survives tail events better than balanced.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.3fr', gap: 24 }} className="barbell-grid">
          {/* INPUTS */}
          <div style={{ padding: '28px', background: 'var(--sp-soft-charcoal)', border: '1px solid var(--sp-divider)', borderRadius: 'var(--sp-radius-card)' }}>
            <h3 style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--sp-aged-brass)', margin: '0 0 22px 0' }}>Inputs</h3>

            <Field label="Total capital to allocate" suffix="$">
              <NumberInput value={totalCapital} onChange={setTotalCapital} step={10000} min={0} />
            </Field>

            <Field label="Time horizon" suffix="yr">
              <NumberInput value={horizon} onChange={setHorizon} step={1} min={1} max={60} />
            </Field>

            <Field label="Per-bet tail risk budget" suffix="%" hint="Max % of risk side per single asymmetric bet (Taleb: 1-2%)">
              <NumberInput value={tailRiskBudget} onChange={setTailRiskBudget} step={0.5} min={0.5} max={20} />
            </Field>

            <div style={{ marginTop: 24 }}>
              <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'rgba(232, 221, 196, 0.55)', marginBottom: 10 }}>
                Risk Profile
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {RISK_PROFILES.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setProfile(p.id)}
                    style={{
                      textAlign: 'left',
                      padding: '12px 14px',
                      background: profile === p.id ? 'var(--sp-brass-bg)' : 'transparent',
                      border: `1px solid ${profile === p.id ? 'var(--sp-aged-brass)' : 'var(--sp-divider)'}`,
                      borderRadius: 'var(--sp-radius)',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <span style={{ fontFamily: 'var(--sp-font-serif)', fontSize: 14, color: profile === p.id ? 'var(--sp-warm-cream)' : 'rgba(232, 221, 196, 0.85)' }}>{p.label}</span>
                      <span style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 11, color: profile === p.id ? 'var(--sp-aged-brass)' : 'rgba(232, 221, 196, 0.50)' }}>
                        {p.safePct}/{100 - p.safePct}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, lineHeight: 1.4, color: 'rgba(232, 221, 196, 0.60)' }}>{p.hint}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* OUTPUTS */}
          <div>
            {/* Visual barbell */}
            <div style={{ padding: '28px', background: 'linear-gradient(135deg, var(--sp-card-charcoal) 0%, var(--sp-soft-charcoal) 100%)', border: '1px solid var(--sp-aged-brass)', borderRadius: 'var(--sp-radius-card)', marginBottom: 16 }}>
              <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--sp-aged-brass)', marginBottom: 18 }}>
                Your Barbell
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
                {/* SAFE side */}
                <div style={{ padding: '20px', background: 'rgba(58, 84, 71, 0.18)', border: '1px solid rgba(58, 84, 71, 0.40)', borderRadius: 'var(--sp-radius-card)' }}>
                  <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, color: 'var(--sp-forest-moss)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 8 }}>
                    Extreme Safe ({result.safePct}%)
                  </div>
                  <div style={{ fontFamily: 'var(--sp-font-serif)', fontSize: 28, lineHeight: 1.1, color: 'var(--sp-warm-cream)', marginBottom: 4 }}>
                    ${result.safeAmount.toLocaleString()}
                  </div>
                  <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 11, color: 'var(--sp-forest-moss)' }}>
                    Survives any tail event
                  </div>
                </div>

                {/* RISK side */}
                <div style={{ padding: '20px', background: 'rgba(74, 31, 31, 0.20)', border: '1px solid rgba(74, 31, 31, 0.40)', borderRadius: 'var(--sp-radius-card)' }}>
                  <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, color: 'var(--sp-oxblood)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 8 }}>
                    Extreme Risk ({100 - result.safePct}%)
                  </div>
                  <div style={{ fontFamily: 'var(--sp-font-serif)', fontSize: 28, lineHeight: 1.1, color: 'var(--sp-warm-cream)', marginBottom: 4 }}>
                    ${result.riskAmount.toLocaleString()}
                  </div>
                  <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 11, color: 'var(--sp-oxblood)' }}>
                    {result.maxBets} asymmetric bets max
                  </div>
                </div>
              </div>

              <div style={{ borderTop: '1px solid var(--sp-divider)', paddingTop: 16, fontFamily: 'var(--sp-font-serif)', fontStyle: 'italic', fontSize: 14, lineHeight: 1.55, color: 'var(--sp-warm-cream)', borderLeft: '2px solid var(--sp-aged-brass)', paddingLeft: 14 }}>
                {result.verdict}
              </div>
            </div>

            {/* Specific allocation suggestions */}
            <div style={{ padding: '24px 28px', background: 'var(--sp-soft-charcoal)', border: '1px solid var(--sp-divider)', borderRadius: 'var(--sp-radius-card)', marginBottom: 16 }}>
              <h3 style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--sp-aged-brass)', margin: '0 0 18px 0' }}>
                Specific allocations
              </h3>

              <div style={{ marginBottom: 18 }}>
                <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 11, color: 'var(--sp-forest-moss)', textTransform: 'uppercase', letterSpacing: '0.10em', marginBottom: 8 }}>
                  Extreme Safe (${result.safeAmount.toLocaleString()})
                </div>
                <AllocRow label="T-bills / short-term Treasuries" pct={50} amount={result.safeAmount * 0.5} />
                <AllocRow label="Cash + money market (immediate liquidity)" pct={20} amount={result.safeAmount * 0.2} />
                <AllocRow label="Gold / hard assets" pct={20} amount={result.safeAmount * 0.2} />
                <AllocRow label="TIPS (inflation-linked)" pct={10} amount={result.safeAmount * 0.1} />
              </div>

              <div>
                <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 11, color: 'var(--sp-oxblood)', textTransform: 'uppercase', letterSpacing: '0.10em', marginBottom: 8 }}>
                  Extreme Risk (${result.riskAmount.toLocaleString()})
                </div>
                <AllocRow label="Concentrated equity bets (3-5 names)" pct={40} amount={result.riskAmount * 0.4} />
                <AllocRow label="Venture / early-stage exposure" pct={25} amount={result.riskAmount * 0.25} />
                <AllocRow label="Tail-risk hedges / volatility plays" pct={15} amount={result.riskAmount * 0.15} />
                <AllocRow label="Crypto (BTC + ETH allocation)" pct={10} amount={result.riskAmount * 0.10} />
                <AllocRow label="Special situations / distressed" pct={10} amount={result.riskAmount * 0.10} />
              </div>
            </div>

            {/* Honest read */}
            <div style={{ padding: '20px 24px', background: 'var(--sp-parchment-bg)', border: '1px solid var(--sp-divider)', borderRadius: 'var(--sp-radius-card)' }}>
              <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.16em', color: 'var(--sp-aged-brass)', marginBottom: 10 }}>
                Honest Read
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.6, color: 'rgba(232, 221, 196, 0.78)' }}>
                {result.honestRead}
              </div>
            </div>

            <div style={{ marginTop: 16, fontSize: 12, color: 'rgba(232, 221, 196, 0.55)' }}>
              <span style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(232, 221, 196, 0.45)', marginRight: 8 }}>Connected dossiers:</span>
              <Link href="/strategic-playbook/taleb-antifragile" style={{ color: 'var(--sp-aged-brass)' }}>Taleb</Link>
              {' · '}
              <Link href="/strategic-playbook/banking-crisis-shock-playbook" style={{ color: 'var(--sp-aged-brass)' }}>Banking Crisis</Link>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @media (max-width: 900px) {
          .barbell-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}

function allocate({ totalCapital, profile, horizon, tailRiskBudget }) {
  const profileObj = RISK_PROFILES.find((p) => p.id === profile) || RISK_PROFILES[1];
  const safePct = profileObj.safePct;
  const safeAmount = Math.round(totalCapital * (safePct / 100));
  const riskAmount = Math.round(totalCapital * ((100 - safePct) / 100));
  const maxBets = Math.floor(1 / tailRiskBudget);

  let verdict, honestRead;

  if (safePct >= 80) {
    verdict = `Classic Taleb barbell: ${safePct}% in extreme-safe instruments survives any tail event, ${100 - safePct}% in asymmetric bets captures upside.`;
    honestRead = `This allocation will underperform in normal markets vs all-equity portfolios. That's the cost of antifragility. The trade-off: you sleep through 2008 / 2020 / 2022 declines and capture upside via the risk side. Most who switch from balanced (60/40) to barbell (80/20) struggle with the underperformance during quiet years — that's when most abandon the strategy and miss the tail event when it comes.`;
  } else if (safePct >= 70) {
    verdict = `Moderate barbell: ${safePct}/${100 - safePct}. Balances Taleb-style protection with growth exposure.`;
    honestRead = `Less protective than 80/20 but still avoids the fragile middle. Useful when horizon is long (20+ years) and growth assumption is needed. Watch the temptation to increase risk side after good years.`;
  } else if (safePct >= 60) {
    verdict = `Aggressive barbell: ${safePct}/${100 - safePct}. Tilted to risk side. Requires longer horizon + higher tail-risk tolerance.`;
    honestRead = `Tilted toward growth. Will outperform 80/20 in normal markets but suffer harder in tail events. Only sustainable if you have 20+ year horizon AND emotional tolerance for 50%+ drawdowns on the risk side. Most can't.`;
  } else {
    verdict = `Pure asymmetric: ${safePct}/${100 - safePct}. Founder / early-career profile. High risk, high potential.`;
    honestRead = `This isn't really a barbell anymore — it's a growth portfolio with cash buffer. Works if your career income is the actual "safe" side compounding alongside. Re-evaluate when career income concentration changes.`;
  }

  return {
    safePct,
    safeAmount,
    riskAmount,
    maxBets,
    verdict,
    honestRead,
  };
}

function AllocRow({ label, pct, amount }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 12 }}>
      <span style={{ color: 'rgba(232, 221, 196, 0.78)' }}>
        <span style={{ color: 'var(--sp-aged-brass)', fontFamily: 'var(--sp-font-mono)', fontSize: 11, marginRight: 8 }}>{pct}%</span>
        {label}
      </span>
      <span style={{ fontFamily: 'var(--sp-font-mono)', color: 'var(--sp-warm-cream)' }}>${Math.round(amount).toLocaleString()}</span>
    </div>
  );
}

function Field({ label, suffix, hint, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'rgba(232, 221, 196, 0.55)', marginBottom: 6, display: 'flex', justifyContent: 'space-between' }}>
        <span>{label}</span>
        {suffix && <span style={{ color: 'var(--sp-aged-brass)' }}>{suffix}</span>}
      </div>
      {children}
      {hint && <div style={{ fontSize: 11, color: 'rgba(232, 221, 196, 0.50)', marginTop: 4, lineHeight: 1.5 }}>{hint}</div>}
    </div>
  );
}

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  background: 'var(--sp-charcoal-black)',
  border: '1px solid var(--sp-divider)',
  borderRadius: 'var(--sp-radius)',
  color: 'var(--sp-warm-cream)',
  fontFamily: 'var(--sp-font-body)',
  fontSize: 14,
  outline: 'none',
};

function NumberInput({ value, onChange, step, min, max }) {
  return (
    <input
      type="number"
      value={value}
      step={step}
      min={min}
      max={max}
      onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
      style={inputStyle}
    />
  );
}

function ToolTopbar() {
  return (
    <div className="sp-topbar">
      <div>
        <h1>Strategic Intelligence</h1>
        <div className="sp-topbar-meta">Tools · Barbell Allocator</div>
      </div>
      <nav className="sp-topbar-nav">
        <Link href="/strategic-playbook">Dashboard</Link>
        <Link href="/strategic-playbook/library">Library</Link>
        <Link href="/strategic-playbook/tools" className="sp-active">Tools</Link>
        <Link href="/strategic-playbook/decisions">Decisions</Link>
        <Link href="/strategic-playbook/simulate">Simulate</Link>
      </nav>
    </div>
  );
}
